/**
 * AbrirTarpModal (GUPM-174 revisão 2 + GUPM-178 Fase B) — abertura em UM
 * formulário só, agora com rascunho persistente "Em abertura".
 * Fiel ao `finishTarp` do protótipo: o registro nasce como rascunho ao começar
 * a preencher (barragem escolhida) e só vira "aberta de verdade" ao confirmar.
 * Layout (cima → baixo): identidade/chip · barragem (1º campo) · checklist guiado
 * (TarpFlowRunner) · observação · footer (Descartar rascunho · Manter rascunho/Sair
 * · Abrir). Fechar no ×/overlay com rascunho em jogo pede confirmação (GUPM-262).
 *
 * Presentacional: os callbacks de rascunho (onChange/onDiscard) são opcionais —
 * sem eles, funciona como confirm direto (fallback e testes). O orquestrador
 * (M3 index) liga o `useTarpDraft` (autosave debounced + confirmar/descartar).
 */
import { useState } from 'react';
import { Button, LEVEL_UI } from '@/ui';
import { usePopup } from '@/hooks/usePopup';
import {
  botaoRegistrarLabel, canRegistrar, fluxoDaTarp, subtituloDaTarp, tarpEntry, tituloDaTarp, type InstrumentType,
} from '@/domain';
import type { FlowState, Outcome } from '@/domain/tarp/flow';
import type { AutoOpenOrigem } from '@/domain/tarp/disparo';
import type { QuickAction } from '@/components/tarp';
import { DAM_UNIDENTIFIED } from '@/services/datasources';
import type { DamOption, OpenTarpInput, TarpRecord } from '@/services/datasources';
import { tarpCodeLabel, tarpCodeLevel } from '../catalog-stub';
import { FIELD_CLS, Modal } from './Modal';
import { TarpFlowRunner } from './TarpFlowRunner';
import { TarpStatusPill } from './TarpStatusPill';
import { rotuloDaEstrutura } from '@/domain/estrutura/rotulo';

/** Mudança reportada ao orquestrador para autosave do rascunho. */
export interface AbrirTarpChange {
  /** null = "Ainda não identificada" (GUPM-186); o sentinel do select já foi traduzido. */
  damId: string | null;
  flowState?: FlowState;
  observation?: string;
  stepsCompleted?: number;
}

/**
 * Pré-seleção vinda de uma notificação (GUPM-185): barragem + instrumento chegam
 * prontos (modo notificação). Ausente = fluxo manual (GUPM-174: instrumento
 * identificado durante o protocolo).
 *
 * `origem` diz QUAL notificação — GUPM-303. Sem ela a legenda creditava toda
 * pré-seleção a uma "leitura anômala", inclusive a da TARP de perda de sinal, onde
 * não há leitura nenhuma: o instrumento está mudo.
 */
export interface AbrirTarpPreselect {
  damId: string;
  instrumentId: string;
  instrumentCode: string;
  origem: AutoOpenOrigem;
  /** C-88 — tipo do instrumento: título com o tipo e releitura adaptada no tiltímetro. */
  instrumentType?: InstrumentType;
}

/**
 * Como a legenda nomeia o gatilho da notificação — GUPM-303. `Record<AutoOpenOrigem,…>`
 * e não literal solto: gatilho novo quebra o build aqui, em vez de herdar em silêncio o
 * texto do gatilho anterior — que é exatamente como "leitura anômala" foi parar na TARP
 * do instrumento mudo.
 */
const MOTIVO_DA_NOTIFICACAO: Record<AutoOpenOrigem, string> = {
  leitura: 'leitura anômala',
  'perda-de-sinal': 'perda de sinal',
};

// Texto-guia do rodapé por DESFECHO do fluxo (fiel ao V8). `Record<Outcome,…>`
// força cobrir todo desfecho novo (exaustividade checada pelo TS).
const HINT_BY_OUTCOME: Record<Outcome, string> = {
  rotina: 'Nada anormal a registrar — descarte ou mantenha o rascunho.',
  paebm: 'Emergência — registrar aciona o PAEBM',
  handoff: 'Pronto — registrar encaminha ao RT',
  fim: 'Pronto para concluir o registro',
};

// Cor do botão terminal por desfecho — fiel ao `updateTarpFinish` do V8 (GUPM-207):
// paebm = vermelho de emergência (#ef4444/branco), handoff = azul técnico (#78B4CF),
// fim = verde. `rotina` não tem botão (só descartar/cancelar).
const VARIANT_BY_OUTCOME: Record<Outcome, 'danger' | 'info' | 'success' | 'primary'> = {
  paebm: 'danger',
  handoff: 'info',
  fim: 'success',
  rotina: 'primary',
};

/**
 * Mensagem-guia do rodapé. Primeiro os estados de formulário (sem barragem,
 * checklist ainda em curso); resolvido o desfecho, o texto vem do lookup por
 * `Outcome`.
 */
function computeHint(damId: string, flowOutcome: Outcome | null): string {
  if (!damId) return 'Selecione a barragem para começar';
  if (flowOutcome === null) return 'Percorra o checklist até o final para concluir';
  return HINT_BY_OUTCOME[flowOutcome];
}

export function AbrirTarpModal({
  action,
  dams,
  draft,
  onConfirm,
  onCancel,
  onChange,
  onDiscard,
  onKeep,
  hasPendingChanges = false,
  canDiscard = false,
  saving = false,
  saveError = null,
  submitting,
  error,
  preselect = null,
}: {
  action: QuickAction;
  dams: DamOption[];
  /** Rascunho existente (GUPM-178) — hidrata barragem/checklist/observação ao retomar. */
  draft?: TarpRecord | null;
  /** GUPM-185: barragem+instrumento pré-preenchidos por notificação (modo notificação). */
  preselect?: AbrirTarpPreselect | null;
  onConfirm: (input: OpenTarpInput, outcome: Outcome) => void;
  /** Fechar SEM rascunho em jogo (nada persistido/pendente) — fecha direto. */
  onCancel: () => void;
  /** GUPM-178: reporta cada mudança para autosave do rascunho "Em abertura". */
  onChange?: (change: AbrirTarpChange) => void;
  /** GUPM-178: descarta o rascunho (só o autor — a UI espelha a RLS). */
  onDiscard?: () => void;
  /**
   * GUPM-262: "Manter rascunho" — o dono (AbrirTarpFlow) faz flush do autosave
   * pendente antes de fechar. Sem `onKeep`, cai no `onCancel` (compat).
   */
  onKeep?: () => void;
  /**
   * GUPM-293: há alteração pendente de autosave (debounce não gravado ainda).
   * Antes era "existe rascunho OU há pendência" — o que fazia um rascunho já salvo
   * e intocado pedir cerimônia para fechar.
   */
  hasPendingChanges?: boolean;
  /** GUPM-178: se o usuário atual pode descartar (autor + EM_ABERTURA). */
  canDiscard?: boolean;
  /** GUPM-178: autosave em andamento (indicador honesto no lugar de "salvo" fixo). */
  saving?: boolean;
  /** GUPM-178: falha do autosave (a promessa "não perder progresso" não pode mentir). */
  saveError?: string | null;
  submitting: boolean;
  error: string | null;
}) {
  // A barragem só é travada no modo NOTIFICAÇÃO, onde ela vem da leitura anômala —
  // fato apurado, não suposição. Nenhum acionamento fixa barragem pelo próprio código:
  // o GUPM-207 fixava a B no TARP-PS-TILT sob a premissa de que tiltímetro existia só
  // na B, e a premissa era falsa (GUPM-304 — 10 tiltímetros ativos nas três estruturas,
  // os de A e C cadastrados três semanas ANTES daquela trava).
  const damLocked = !!preselect;

  // Estado guarda o valor CRU do select: '' (nada), um `dams.id`, ou o sentinel
  // DAM_UNIDENTIFIED. Ao retomar um rascunho sem barragem (dam_id null), volta ao
  // sentinel para reexibir "Ainda não identificada" (GUPM-186).
  const [damId, setDamId] = useState<string>(
    draft ? (draft.damId ?? DAM_UNIDENTIFIED) : (preselect?.damId ?? ''),
  );
  const [observation, setObservation] = useState(draft?.observation ?? '');
  const [flowState, setFlowState] = useState<FlowState | null>(draft?.flowState ?? null);
  const [flowOutcome, setFlowOutcome] = useState<Outcome | null>(null);
  const entry = tarpEntry(action.code);
  const u = LEVEL_UI[tarpCodeLevel(action.code)];
  // C-88 — o tipo vem do auto-open ou, ao retomar um rascunho, da própria TARP.
  const tipo = preselect?.instrumentType ?? draft?.instrumentType ?? null;
  const flow = fluxoDaTarp(action.code, tipo);
  // GUPM-190 T1/T2: 'rotina' (nada anormal) NÃO registra — só descartar/cancelar.
  const registrable = canRegistrar(flowOutcome);
  const isRotina = flowOutcome === 'rotina';
  const canOpen = damId !== '' && registrable && !submitting;
  const registrarLabel = flowOutcome && registrable ? botaoRegistrarLabel(flowOutcome) : 'Concluir Registro de TARP';

  // Autosave event-driven: reporta a mudança do formulário para o orquestrador
  // persistir o rascunho. Só a partir de uma ação real (nunca no mount/hidratação,
  // que regrediria o rascunho) e só com barragem escolhida. Mescla o valor novo
  // com o estado atual do formulário.
  function report(next: { damId?: string; observation?: string; flowState?: FlowState | null }) {
    const nextDamId = next.damId ?? damId;
    if (!nextDamId) return; // '' = nada selecionado ainda (o sentinel é truthy → autosalva)
    const nextFlow = next.flowState !== undefined ? next.flowState : flowState;
    onChange?.({
      // Traduz o sentinel para null real só no payload (o guard acima usa o valor cru).
      damId: nextDamId === DAM_UNIDENTIFIED ? null : nextDamId,
      flowState: nextFlow ?? undefined,
      observation: next.observation ?? observation,
      stepsCompleted: nextFlow ? nextFlow.path.length : 0,
    });
  }

  const hint = computeHint(damId, flowOutcome);

  // GUPM-262: ×/clique-fora não fecham mais mudos. Com rascunho em jogo e
  // permissão de descarte, pergunta (manter × descartar); sem permissão,
  // mantém (flush) e fecha; sem rascunho, fecha direto.
  const [confirmClose, setConfirmClose] = useState(false);
  const keep = onKeep ?? onCancel;
  function requestClose() {
    if (!hasPendingChanges) {
      onCancel();
      return;
    }
    if (canDiscard && onDiscard) {
      setConfirmClose(true);
      return;
    }
    keep();
  }

  return (
    <Modal
      title={tituloDaTarp(action.code, tipo) ?? tarpCodeLabel(action.code, tipo)}
      subtitle={subtituloDaTarp(action.code, tipo) ?? undefined}
      accent={u.color}
      headerRight={<TarpStatusPill status="EM_ABERTURA" />}
      onClose={requestClose}
      footer={
        <div className="space-y-2">
          <p className={`text-[11px] ${canOpen ? 'text-level-normal' : 'text-jf-dim'}`}>{hint}</p>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {canDiscard && onDiscard && (
                <Button variant="ghost" onClick={onDiscard}>
                  <span className="text-red-400">Descartar rascunho</span>
                </Button>
              )}
              {/* GUPM-262: o antigo "Cancelar" mantinha o rascunho — rótulo dizia o
                  contrário do efeito. GUPM-293: sem alteração nenhuma é "Fechar" —
                  um rascunho persistido e intocado já está mantido.
                  GUPM-298: quem alterou vê "Manter alterações", não "Manter rascunho"
                  — é a alteração dele que está em jogo, não a existência do rascunho.
                  "Salvar" seria mentira enquanto o autosave existir: quando a pessoa
                  lê o botão, já está gravado. O "Salvar" de verdade chega junto com a
                  gravação sob demanda (redesenho do ciclo do rascunho). */}
              <Button variant="ghost" onClick={hasPendingChanges ? keep : onCancel}>
                {hasPendingChanges ? 'Manter alterações' : 'Fechar'}
              </Button>
            </div>
            {/* GUPM-190 T1: em desfecho 'rotina' (nada anormal) não há registro — só descartar/cancelar. */}
            {!isRotina && (
              <Button
                // GUPM-207: cor do botão fiel ao V8 (`updateTarpFinish`) — por desfecho
                // (paebm=vermelho, handoff=azul técnico, fim=verde), não o amarelo padrão.
                variant={flowOutcome ? VARIANT_BY_OUTCOME[flowOutcome] : 'primary'}
                disabled={!canOpen}
                onClick={() =>
                  flowOutcome &&
                  registrable &&
                  onConfirm(
                    {
                      damId: damId === DAM_UNIDENTIFIED ? null : damId,
                      instrumentId: preselect?.instrumentId ?? null,
                      tarpCode: action.code,
                    },
                    flowOutcome,
                  )
                }
              >
                {submitting ? 'Abrindo…' : registrarLabel}
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        {/* Warning do protocolo — banner de alerta no topo (fiel ao V8 #modal-warning). */}
        {entry?.warning && (
          <div
            className="rounded-lg border px-3 py-2 text-[13px] font-semibold leading-snug"
            style={{ borderColor: u.border, background: u.bg, color: u.color }}
          >
            {entry.warning}
          </div>
        )}

        {/* Protocolo — a barragem é o 1º passo do fluxo (destrava o resto). GUPM-190 T8. */}
        <div className="border-t border-jf-border pt-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs font-bold uppercase text-jf-dim">Protocolo</div>
            <span className={`text-[10px] ${saveError ? 'text-level-emergencia' : 'text-jf-dim'}`}>
              {saveError ? '⚠ Falha ao salvar' : saving ? 'Salvando…' : 'Salvo automaticamente'}
            </span>
          </div>

          {/* Passo 1 — Barragem: destrava o protocolo. Cartão no estilo dos passos do
              fluxo: destaque (amarelo) enquanto vazio; verde ✓ ao escolher. */}
          <div
            className={`rounded-lg border p-3 ${
              damId
                ? 'border-level-normal/40 bg-level-normal/[0.05]'
                : 'border-jf-yellow bg-jf-yellow/[0.06]'
            }`}
          >
            <div className="mb-1.5 flex items-center gap-2">
              <span className="rounded border border-slate-300/40 bg-slate-300/10 px-1.5 py-0.5 text-[9px] font-extrabold tracking-wider text-slate-300">
                BARRAGEM
              </span>
              {damId && <span className="ml-auto text-xs text-level-normal">✓</span>}
            </div>
            {!damId && (
              <p className="mb-2 text-[13px] leading-snug text-slate-200">
                Selecione a barragem para iniciar o fluxo de ações.
              </p>
            )}
            <select
              aria-label="Barragem"
              className={FIELD_CLS}
              value={damId}
              disabled={damLocked}
              onChange={(e) => {
                setDamId(e.target.value);
                report({ damId: e.target.value });
              }}
            >
              <option value="">Selecione…</option>
              {dams.map((d) => (
                <option key={d.id} value={d.id}>
                  {rotuloDaEstrutura(d.code) ?? d.code}
                </option>
              ))}
              <option value={DAM_UNIDENTIFIED}>Ainda não identificada</option>
            </select>
            {preselect ? (
              <p className="mt-1 text-[10px] text-jf-dim">
                Instrumento <span className="font-bold text-jf-muted">{preselect.instrumentCode}</span> e
                barragem vêm da notificação ({MOTIVO_DA_NOTIFICACAO[preselect.origem]}) — pré-preenchidos.
              </p>
            ) : (
              <p className="mt-1 text-[10px] text-jf-dim">
                O instrumento é identificado durante o protocolo — o acionamento não fixa instrumento.
              </p>
            )}
          </div>

          {/* Passos do protocolo — visíveis mas travados até escolher a barragem. */}
          {flow ? (
            <div className="mt-2">
              <TarpFlowRunner
                flow={flow}
                initialState={draft?.flowState ?? undefined}
                mode={damId ? 'edit' : 'locked'}
                onOutcomeChange={setFlowOutcome}
                onStateChange={(s) => {
                  setFlowState(s);
                  report({ flowState: s });
                }}
              />
            </div>
          ) : (
            <p className="mt-2 text-xs text-jf-muted">Este acionamento não possui checklist guiado.</p>
          )}
        </div>

        {/* Observação — texto livre persistido junto do rascunho */}
        <div className="border-t border-jf-border pt-3">
          <label className="mb-1 block text-xs font-bold uppercase text-jf-dim">
            Observação <span className="text-jf-dim/70">— opcional</span>
          </label>
          <textarea
            className={FIELD_CLS}
            rows={2}
            value={observation}
            onChange={(e) => {
              setObservation(e.target.value);
              report({ observation: e.target.value });
            }}
            placeholder="Anote o que observou (persistido no rascunho)…"
          />
        </div>

        {error && (
          <p role="alert" className="text-xs text-level-emergencia">
            {error}
          </p>
        )}

        {/* GUPM-262: confirmação ao fechar com rascunho em jogo (×/clique-fora/Esc). */}
        {confirmClose && (
          <ConfirmarFechamento
            onVoltar={() => setConfirmClose(false)}
            onDescartar={() => {
              setConfirmClose(false);
              onDiscard?.();
            }}
            onManter={() => {
              setConfirmClose(false);
              keep();
            }}
          />
        )}
      </div>
    </Modal>
  );
}

/**
 * "O que fazer com o rascunho?" — GUPM-262. Esc = "Voltar" (C-91): volta ao fluxo,
 * sem descartar nem manter.
 */
function ConfirmarFechamento({
  onVoltar,
  onDescartar,
  onManter,
}: {
  onVoltar: () => void;
  onDescartar: () => void;
  onManter: () => void;
}) {
  const ref = usePopup<HTMLDivElement>({ aoFechar: onVoltar });
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label="O que fazer com o rascunho?"
        tabIndex={-1}
        className="w-full max-w-sm space-y-3 rounded-xl border-2 border-jf-border bg-jf-surface p-4 focus:outline-none"
      >
        <h3 className="text-sm font-black text-white">O que fazer com o rascunho?</h3>
        <p className="text-xs text-jf-muted">
          Mantido, ele continua na lista de TARPs como “Em abertura” para qualquer
          plantonista retomar. Descartado, some da lista.
        </p>
        <div className="flex flex-wrap justify-end gap-2">
          {/* autoFocus: o foco começa na saída segura. */}
          <Button variant="ghost" autoFocus onClick={onVoltar}>
            Voltar
          </Button>
          <Button variant="ghost" onClick={onDescartar}>
            <span className="text-red-400">Descartar rascunho</span>
          </Button>
          <Button variant="primary" onClick={onManter}>
            Manter alterações
          </Button>
        </div>
      </div>
    </div>
  );
}
